import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  ExportFiltersInput,
  Order,
  PatchAdminV1StoresStoreIdOrdersIdBody,
  PatchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdBody,
  PostAdminV1StoresStoreIdOrdersIdCancelBody,
  PostAdminV1StoresStoreIdOrdersIdDesignBody,
  PostAdminV1StoresStoreIdOrdersIdFulfillmentsBody,
  PostAdminV1StoresStoreIdOrdersIdHoldBody,
  PostAdminV1StoresStoreIdOrdersIdMarkPaidBody,
  PostAdminV1StoresStoreIdOrdersIdNotesBody,
  PatchAdminV1StoresStoreIdOrdersIdLinesLineIdBody,
  PostAdminV1StoresStoreIdOrdersBulkBody,
  PostAdminV1StoresStoreIdOrdersIdApprovalRecordBody,
  PostAdminV1StoresStoreIdOrdersIdEmailsResendBody,
  PostAdminV1StoresStoreIdOrdersIdRefundsBody,
} from '@nucleus-commerce/api-client/admin';

import { ordersApi } from '../api/orders-api';

import { orderKeys } from './order-keys';

/**
 * Every action answers with the whole order, so the detail is replaced and the lists refetch.
 * Emails go out from the worker after the fact, so the notifications list is refetched too.
 */
function remember(queryClient: ReturnType<typeof useQueryClient>, storeId: string, order: Order) {
  queryClient.setQueryData(orderKeys.detail(storeId, order.id), order);
  void queryClient.invalidateQueries({ queryKey: orderKeys.notifications(storeId, order.id) });
  return queryClient.invalidateQueries({ queryKey: orderKeys.lists(storeId) });
}

/** 409 (not awaiting payment) and 422 (above the balance) are explained by the dialog. */
export function useMarkOrderPaid(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: PostAdminV1StoresStoreIdOrdersIdMarkPaidBody;
    }) => ordersApi.markPaid(storeId, id, body),
    meta: { successMessage: 'orders:toasts.markedPaid', silentError: true },
    onSuccess: (order) => remember(queryClient, storeId, order),
  });
}

export function useCancelOrder(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PostAdminV1StoresStoreIdOrdersIdCancelBody }) =>
      ordersApi.cancel(storeId, id, body),
    meta: { successMessage: 'orders:toasts.cancelled', silentError: true },
    onSuccess: (order) => remember(queryClient, storeId, order),
  });
}

export function useAddOrderNote(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PostAdminV1StoresStoreIdOrdersIdNotesBody }) =>
      ordersApi.addNote(storeId, id, body),
    meta: { successMessage: 'orders:toasts.noteAdded', silentError: true },
    onSuccess: (order) => remember(queryClient, storeId, order),
  });
}

/** Tags and staff notes save quietly from the page; the address dialog passes its own toast. */
export function useUpdateOrder(storeId: string, successMessage?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdOrdersIdBody }) =>
      ordersApi.update(storeId, id, body),
    meta: { successMessage, silentError: true },
    onSuccess: (order) => remember(queryClient, storeId, order),
  });
}

export function useHoldOrder(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PostAdminV1StoresStoreIdOrdersIdHoldBody }) =>
      ordersApi.hold(storeId, id, body),
    meta: { successMessage: 'orders:toasts.held', silentError: true },
    onSuccess: (order) => remember(queryClient, storeId, order),
  });
}

export function useReleaseOrder(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, force = false }: { id: string; force?: boolean }) =>
      ordersApi.release(storeId, id, force),
    meta: { successMessage: 'orders:toasts.released', silentError: true },
    onSuccess: (order) => remember(queryClient, storeId, order),
  });
}

/** 422 on a line quantity above what is open, 409 while the order is on hold or cancelled. */
export function useCreateFulfillment(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: PostAdminV1StoresStoreIdOrdersIdFulfillmentsBody;
    }) => ordersApi.fulfill(storeId, id, body),
    meta: { successMessage: 'orders:toasts.fulfilled', silentError: true },
    onSuccess: (order) => remember(queryClient, storeId, order),
  });
}

/** Tracking edits and the pending → shipped → delivered steps; 409 on a step backwards. */
export function useUpdateFulfillment(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      fulfillmentId,
      body,
    }: {
      id: string;
      fulfillmentId: string;
      body: PatchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdBody;
    }) => ordersApi.updateFulfillment(storeId, id, fulfillmentId, body),
    meta: { successMessage: 'orders:toasts.fulfillmentUpdated', silentError: true },
    onSuccess: (order) => remember(queryClient, storeId, order),
  });
}

/** 409 when nothing is refundable, 422 above the refundable balance. */
export function useRefundOrder(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PostAdminV1StoresStoreIdOrdersIdRefundsBody }) =>
      ordersApi.refund(storeId, id, body),
    meta: { successMessage: 'orders:toasts.refunded', silentError: true },
    onSuccess: (order) => remember(queryClient, storeId, order),
  });
}

/**
 * 409 once the order is closed, needs no design or the design is already approved; 404 when the
 * media row is not in this organisation's library.
 */
export function useSendDesign(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PostAdminV1StoresStoreIdOrdersIdDesignBody }) =>
      ordersApi.sendDesign(storeId, id, body),
    meta: { successMessage: 'orders:toasts.designSent', silentError: true },
    onSuccess: (order) => remember(queryClient, storeId, order),
  });
}

/** 409 when the order needs no approval or is already approved; 422 when changes come without a note. */
export function useRecordApproval(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: PostAdminV1StoresStoreIdOrdersIdApprovalRecordBody;
    }) => ordersApi.recordApproval(storeId, id, body),
    meta: { successMessage: 'orders:toasts.approvalRecorded', silentError: true },
    onSuccess: (order) => remember(queryClient, storeId, order),
  });
}

/** Completed or cancelled orders only; 409 otherwise. */
export function useArchiveOrder(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => ordersApi.archive(storeId, id),
    meta: { successMessage: 'orders:toasts.archived' },
    onSuccess: (order) => remember(queryClient, storeId, order),
  });
}

/** 409 ORDER_LINE_PRICE_WOULD_CHANGE and 409 for shipped lines are explained by the dialog. */
export function useUpdateOrderLine(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      lineId,
      body,
    }: {
      id: string;
      lineId: string;
      body: PatchAdminV1StoresStoreIdOrdersIdLinesLineIdBody;
    }) => ordersApi.updateLine(storeId, id, lineId, body),
    meta: { successMessage: 'orders:toasts.lineUpdated', silentError: true },
    onSuccess: (order) => remember(queryClient, storeId, order),
  });
}

export function useCancelFulfillment(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fulfillmentId }: { id: string; fulfillmentId: string }) =>
      ordersApi.cancelFulfillment(storeId, id, fulfillmentId),
    meta: { successMessage: 'orders:toasts.fulfillmentCancelled' },
    onSuccess: (order) => remember(queryClient, storeId, order),
  });
}

/** The send-log row arrives queued; the list refetches so it shows straight away. */
export function useResendOrderEmail(storeId: string, orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdOrdersIdEmailsResendBody) =>
      ordersApi.resendEmail(storeId, orderId, body),
    meta: { successMessage: 'orders:toasts.emailResent', silentError: true },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: orderKeys.notifications(storeId, orderId) }),
  });
}

/** Per-row outcomes come back in the result; every touched order and every list refetches. */
export function useBulkOrders(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdOrdersBulkBody) => ordersApi.bulk(storeId, body),
    meta: { silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: orderKeys.all(storeId) }),
  });
}

/** The job appears on the imports and exports page; the toast links there. */
export function useExportOrders(storeId: string) {
  return useMutation({
    mutationFn: (filters: ExportFiltersInput) => ordersApi.exportOrders(storeId, filters),
    meta: { silentError: false },
  });
}
